import { computed, inject, Injectable, signal } from '@angular/core';
import {
  DataManagementLinksService,
  LinkDto,
  LinkDtoAuthTypeEnum,
  LinkDtoGroupEnum,
  LinkDtoTargetEnum,
} from 'external-links-generated-api';
import { first, Observable, tap } from 'rxjs';
import { FeatureToggleService } from 'shared-data-access';
import { USER_EMAIL, USER_NAME } from 'shared-utils';

@Injectable()
export class LinksService {
  private readonly userEmail = inject(USER_EMAIL);
  private readonly userName = inject(USER_NAME);
  private readonly dataManagementLinksService = inject(DataManagementLinksService);
  private readonly featureToggleService = inject(FeatureToggleService);

  readonly links = signal<LinkDto[]>([]);
  readonly sidebarLinks = computed<LinkDto[]>(
    () => this.links()?.filter((link: LinkDto) => link.group === LinkDtoGroupEnum.Sidebar) || []
  );
  readonly footerLinks = computed<LinkDto[]>(
    () => this.links()?.filter((link: LinkDto) => link.group === LinkDtoGroupEnum.Footer) || []
  );

  getLinks(): Observable<LinkDto[]> {
    return this.dataManagementLinksService.getLinks().pipe(
      first(),
      tap((links: LinkDto[]) => this.links.set(links))
    );
  }

  getSidebarRedirectLinks(): LinkDto[] {
    return this.getFeatureConfig('redirect')
      ? this.mapRedirectLinks(this.sidebarLinks()?.filter((link: LinkDto) => link.target === LinkDtoTargetEnum.Blank))
      : [];
  }

  getSidebarIframeLinks(): LinkDto[] {
    return this.getFeatureConfig('iframe')
      ? this.sidebarLinks().filter((link: LinkDto) => link.target === LinkDtoTargetEnum.Iframe)
      : [];
  }

  private mapRedirectLinks(links: LinkDto[]): LinkDto[] {
    return links.map((link: LinkDto) => ({ ...link, url: this.getRedirectLink(link.url, link.authType) }));
  }

  private getRedirectLink(baseUrl: string, authType: LinkDtoAuthTypeEnum): string {
    if (authType === LinkDtoAuthTypeEnum.None) {
      return baseUrl;
    }

    return this.getRedirectLinkWithAuthToken(baseUrl, authType);
  }

  private getRedirectLinkWithAuthToken(baseUrl: string, authType: LinkDtoAuthTypeEnum): string {
    const tokenString = `tenant=${this.getLinkTokenTenant(authType)}&user=${this.userName()}&email=${this.userEmail()}`;

    return `${baseUrl}?token=${this.toUrlBase64(tokenString)}`;
  }

  private toUrlBase64(str: string): string {
    return encodeURIComponent(btoa(String.fromCodePoint(...new TextEncoder().encode(str))));
  }

  private getFeatureConfig(key: string): boolean {
    return this.featureToggleService.getFeatureConfig('sidebar', 'externalLinks', key);
  }

  private getLinkTokenTenant(authType: LinkDtoAuthTypeEnum): string {
    switch (authType) {
      case LinkDtoAuthTypeEnum.MaterialsManagementDe:
        return 'DE';
      default:
        return '';
    }
  }
}
